import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import ENV from "@/utils/env";
import { cn } from "@/utils/classes";
import { asAspectRatio, asDuration, asFileSize } from "@/utils/format";
import { useSegment } from "@/hooks/use-segment";
import { useConcurrencyQueue } from "@/hooks/use-concurrency-queue";
import { useCreateImageAsset, useCreateVideoAsset, useDeleteAsset, useGetAssets, useUpdateAsset } from "@/api/studio/assets/queries";
import { stageUpload, stageVideoUpload, uploadUpload } from "@/api/studio/uploads/api";
import { getVideoThumbnailUrl } from "@/api/renderer/api";
import type { Asset } from "@/api/studio/assets/types/asset.type";
import { EditElement } from "@/features/studies/components/edit-element";
import { Button } from "../button";
import { Table, TableCell, TableHeader, TableRow } from "../table";
import { Toolbar, ToolbarItem } from "../toolbar";
import { ArrowPathIcon, CheckCircleIcon, ExclamationCircleIcon, EyeSlashIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Progress } from "@/components/ui/progress";
import { Loading } from "@/components/fallbacks/loading";
import { validateUpload, type MediaType } from "./upload-validation";

/* Constants */
const IMAGE_FILE_TYPES = [".jpg", ".jpeg", ".png", ".tiff", ".tif"];
const VIDEO_FILE_TYPES = [".mp4", ".mov", ".webm"];
// Caps how many uploads run at once. Firing them all in parallel means onUploadProgress can
// report 100% (bytes buffered for send) long before the request actually completes, since
// every upload is competing for the same bandwidth — this keeps that gap small.
const MAX_CONCURRENT_UPLOADS = 4;

/* Types */
type UploadStatus = "uploading" | "finishing" | "failed" | "invalid";

interface UploadingAsset {
  key: string;
  file: File;
  name: string;
  width: number;
  height: number;
  status: UploadStatus;
  // Set once the asset has been created — used to match the row against the refreshed asset list
  pipelineKey?: string;
  errorMessage?: string;
  // Percentage (0-100) of the file PUT to the presigned URL, updated via axios's onUploadProgress
  progress?: number;
}

/* Prop Types */
interface AssetUploadDropzoneProps {
  projectId: string;
  studyId: string;
  additionalSegmentProperties?: Record<string, unknown>;
  segmentTrackName: string;
  segmentTrackMediaType: string;
  dragDropVideoUrl: string;
  dragDropVideoClassName?: string;
  dragDropText: string;
  selectAssetsText?: string;
  firstColumnHeader?: string;
  mediaType?: MediaType;
}

/*
 * AssetUploadDropzone component
 * Query-driven drag and drop uploader that uploads immediately and initializes from existing assets.
 */
export const AssetUploadDropzone = ({
  projectId,
  studyId,
  additionalSegmentProperties,
  segmentTrackName,
  segmentTrackMediaType,
  dragDropVideoUrl,
  dragDropVideoClassName,
  dragDropText,
  selectAssetsText = "Select assets",
  firstColumnHeader = "Asset",
  mediaType = "image",
}: AssetUploadDropzoneProps) => {
  /* Refs */
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  /* State */
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingAssets, setUploadingAssets] = useState<Array<UploadingAsset>>([]);
  // Tracked separately from deleteAssetMutation since that mutation is shared across every
  // row — this is what lets each row show its own "Deleting..." status independently.
  const [deletingAssetIds, setDeletingAssetIds] = useState<Set<string>>(new Set());

  /* Hooks */
  const { segmentTrackEvent } = useSegment();
  const { enqueue: enqueueUpload } = useConcurrencyQueue(MAX_CONCURRENT_UPLOADS);

  /* Queries */
  const { data: assetsData, isLoading: isAssetsLoading, isFetching: isAssetsFetching, isError: isAssetsError, refetch: refetchAssets } = useGetAssets(projectId, studyId);
  const createImageAssetMutation = useCreateImageAsset();
  const createVideoAssetMutation = useCreateVideoAsset();
  const updateAssetMutation = useUpdateAsset();
  const deleteAssetMutation = useDeleteAsset();

  /* Derived state */
  const existingAssets = assetsData?.assets ?? [];

  // A row is only considered done once the refreshed asset list contains its pipeline key.
  // Creating an asset invalidates the assets query but the refetch is not awaited, so
  // dropping the row as soon as the create call resolves leaves a window — long on large
  // studies — where the table holds nothing and the dropzone re-prompts the user to upload.
  const uploadedPipelineKeys = new Set(existingAssets.map((asset) => asset.pipelineKey));
  const pendingUploads = uploadingAssets.filter((upload) => !upload.pipelineKey || !uploadedPipelineKeys.has(upload.pipelineKey));

  const hasRows = !!existingAssets.length || !!pendingUploads.length;
  // Hold the loading panel while a getAssets request is in flight with nothing to render,
  // so a slow or cancelled refetch never falls through to the empty state
  const isAwaitingAssets = isAssetsLoading || (isAssetsFetching && !hasRows);
  const isVideo = mediaType === "video";
  const acceptedFileTypes = isVideo ? VIDEO_FILE_TYPES : IMAGE_FILE_TYPES;

  /* Side effects */
  // Prune rows the asset list has caught up with — each one holds a File reference,
  // which matters for video uploads
  useEffect(() => {
    const settledKeys = new Set((assetsData?.assets ?? []).map((asset) => asset.pipelineKey));

    setUploadingAssets((prev) => {
      const next = prev.filter((upload) => !upload.pipelineKey || !settledKeys.has(upload.pipelineKey));
      return next.length === prev.length ? prev : next;
    });
  }, [assetsData]);

  /* Helpers */
  const getImageDimensions = (file: File) => {
    return new Promise<{ width: number; height: number }>((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  /* Upload worker */
  const uploadAsset = useCallback(
    async (asset: UploadingAsset) => {
      // Update the asset status to uploading
      setUploadingAssets((prev) => prev.map((row) => (row.key === asset.key ? { ...row, status: "uploading", errorMessage: undefined, progress: 0 } : row)));

      try {
        // Video assets need to go in a different S3 bucket so we have to use different presigned urls
        const { url, key } = isVideo ? await stageVideoUpload(projectId, studyId) : await stageUpload(projectId, studyId);
        await uploadUpload({
          key,
          file: asset.file,
          url,
          onProgress: (percent) => setUploadingAssets((prev) => prev.map((row) => (row.key === asset.key ? { ...row, progress: percent } : row))),
        });

        // The presigned PUT is done but the asset record is still being created — surface that as its
        // own status so the row doesn't sit at 100% with no visible movement for a couple of seconds
        setUploadingAssets((prev) => prev.map((row) => (row.key === asset.key ? { ...row, status: "finishing" } : row)));

        // Create the asset via the type-specific route so videos enter the correct server-side path
        const createAssetMutation = isVideo ? createVideoAssetMutation : createImageAssetMutation;
        await createAssetMutation.mutateAsync({
          projectId,
          studyId,
          values: {
            pipelineKey: key,
            name: asset.name,
            filename: asset.file.name,
          },
        });

        // Tag the row with its pipeline key rather than removing it — it is dropped once the
        // refreshed asset list contains it, so the table never empties into a gap
        setUploadingAssets((prev) => prev.map((row) => (row.key === asset.key ? { ...row, pipelineKey: key } : row)));

        // Segment event
        segmentTrackEvent("Media uploaded", {
          projectId,
          studyId,
          studyType: "asset",
          fileName: asset.name,
        });
      } catch {
        // Update the asset status to failed and set an error message
        setUploadingAssets((prev) =>
          prev.map((row) => (row.key === asset.key ? { ...row, status: "failed", errorMessage: "We had an issue uploading this asset, please try again." } : row)),
        );
      }
    },
    [createImageAssetMutation, createVideoAssetMutation, isVideo, projectId, segmentTrackEvent, studyId],
  );

  /* Handlers */
  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleAdd = useCallback(
    async (files: File[]) => {
      // Running count of videos that will occupy a slot once staging completes.
      // Tracked locally because setUploadingAssets is async and won't yet reflect
      // files accepted earlier in this same loop.
      let videoCount = existingAssets.length + pendingUploads.filter((row) => row.status !== "invalid").length;

      for (const file of files) {
        const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;

        // In the video flow, reject anything that isn't a supported video type with an
        // inline error and never stage it. This covers both dropped files and files
        // picked past the input's accept hint. Image flows are unaffected.
        if (isVideo && !VIDEO_FILE_TYPES.includes(fileExtension)) {
          setUploadingAssets((prev) => [
            ...prev,
            {
              key: crypto.randomUUID(),
              file,
              name: file.name,
              width: 0,
              height: 0,
              status: "invalid",
              errorMessage: "File type not supported. Please upload .mp4, .mov or .webm files.",
            },
          ]);
          continue;
        }

        const dims = isVideo ? { width: 0, height: 0 } : await getImageDimensions(file);

        // Segment event
        segmentTrackEvent(`${segmentTrackName} ${segmentTrackMediaType} Staged`, {
          ...additionalSegmentProperties,
          fileName: file.name,
          fileSize: file.size,
          width: dims.width,
          height: dims.height,
        });

        // Resolve a validation failure reason (if any). Invalid files are shown as
        // an error row and never staged/uploaded.
        const invalidReason = await validateUpload({ file, mediaType, dimensions: dims, currentVideoCount: videoCount });

        const row: UploadingAsset = {
          key: crypto.randomUUID(),
          file,
          name: file.name,
          width: dims.width,
          height: dims.height,
          status: invalidReason ? "invalid" : "uploading",
          errorMessage: invalidReason ?? undefined,
        };

        // Add the new asset to the uploading list
        setUploadingAssets((prev) => [...prev, row]);

        // If the file is valid, reserve its slot and queue the upload — enqueueUpload caps
        // how many run at once instead of firing every file in the batch in parallel
        if (!invalidReason) {
          if (isVideo) videoCount += 1;
          void enqueueUpload(() => uploadAsset(row));
        }
      }

      // Clear the file input value to allow uploading the same file again if needed
      if (inputRef.current) inputRef.current.value = "";
    },
    [additionalSegmentProperties, enqueueUpload, existingAssets, isVideo, mediaType, pendingUploads, segmentTrackEvent, segmentTrackMediaType, segmentTrackName, uploadAsset],
  );

  const handleRetry = (key: string) => {
    const asset = uploadingAssets.find((row) => row.key === key);
    if (!asset || asset.status === "invalid") return;
    void uploadAsset(asset);
  };

  const handleRemoveUploading = (row: UploadingAsset) => {
    segmentTrackEvent(`${segmentTrackName} Staged ${segmentTrackMediaType} Removed`, {
      ...additionalSegmentProperties,
      fileName: row.name,
    });
    setUploadingAssets((prev) => prev.filter((asset) => asset.key !== row.key));
  };

  const handleRemoveExisting = (asset: Asset) => {
    if (deletingAssetIds.has(asset.id)) return;

    segmentTrackEvent(`${segmentTrackName} ${segmentTrackMediaType} Removed`, {
      ...additionalSegmentProperties,
      fileName: asset.name,
    });

    setDeletingAssetIds((prev) => new Set(prev).add(asset.id));

    deleteAssetMutation.mutate(
      {
        projectId,
        studyId,
        assetId: asset.id,
      },
      {
        onSettled: () => {
          setDeletingAssetIds((prev) => {
            const next = new Set(prev);
            next.delete(asset.id);
            return next;
          });
        },
      },
    );
  };

  const handleClear = async () => {
    segmentTrackEvent(`${segmentTrackName} Staged ${segmentTrackMediaType}s Cleared`, {
      ...additionalSegmentProperties,
    });

    setUploadingAssets([]);

    await Promise.allSettled(
      existingAssets.map((asset) =>
        deleteAssetMutation.mutateAsync({
          projectId,
          studyId,
          assetId: asset.id,
        }),
      ),
    );
  };

  const handleRenameUploading = (key: string, newName: string) => {
    setUploadingAssets((prev) => prev.map((row) => (row.key === key ? { ...row, name: newName } : row)));
  };

  const handleRenameExisting = (asset: Asset, newName: string) => {
    if (newName === asset.name) return;

    segmentTrackEvent(`${segmentTrackName} ${segmentTrackMediaType} Renamed`, {
      ...additionalSegmentProperties,
      from: asset.name,
      to: newName,
    });

    updateAssetMutation.mutate({
      projectId,
      studyId,
      assetId: asset.id,
      values: {
        name: newName,
        filename: asset.filename,
      },
    });
  };

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const files = Array.from(e.dataTransfer.files);
      if (!files.length) return;

      // In the video flow, hand every dropped file to handleAdd — it surfaces
      // unsupported types as inline error rows. Image flows keep the existing
      // behaviour of silently discarding unsupported types.
      if (isVideo) {
        await handleAdd(files);
        return;
      }

      const validFiles = files.filter((file) => {
        const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
        return acceptedFileTypes.some((type) => type.toLowerCase() === fileExtension);
      });

      if (validFiles.length) {
        await handleAdd(validFiles);
      }
    },
    [acceptedFileTypes, isVideo, handleAdd],
  );

  const dragBindings = useMemo(
    () => ({
      onDragEnter: (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current += 1;
        setIsDragging(true);
      },
      onDragOver: (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
      },
      onDragLeave: (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) setIsDragging(false);
      },
      onDrop: handleDrop,
    }),
    [handleDrop],
  );

  /* Render */
  return (
    <>
      {/* Tertiary Toolbar */}
      {hasRows && !isAwaitingAssets && (
        <Toolbar>
          <ToolbarItem>
            <div className="flex flex-row gap-2">
              <Button disabled={!hasRows} onClick={handleClear}>
                Clear All
              </Button>
              <Button variant="primary" onClick={handleClick}>
                <PlusIcon className="size-5 text-neutral" />
                Add
              </Button>
            </div>
          </ToolbarItem>
        </Toolbar>
      )}

      {/* Loading state while fetching existing assets */}
      {isAwaitingAssets && !pendingUploads.length && (
        <div className="w-full flex flex-col justify-center gap-3 py-9 h-96 items-center border-2 border-dashed border-base-300 rounded-lg">
          <video src={dragDropVideoUrl} className={cn("w-36", dragDropVideoClassName)} autoPlay muted loop />
          <h3 className="text-base-content/70">Loading assets...</h3>
        </div>
      )}

      {/* Error state — a failed asset fetch must not fall through to the upload prompt */}
      {!isAwaitingAssets && isAssetsError && !hasRows && (
        <div className="w-full flex flex-col justify-center gap-3 py-9 h-96 items-center border-2 border-dashed border-base-300 rounded-lg">
          <h3 className="text-base-content/70">We had an issue loading the assets for this study, please try again.</h3>
          <Button variant="primary" onClick={() => void refetchAssets()}>
            <ArrowPathIcon className="size-5" />
            Try again
          </Button>
        </div>
      )}

      {/* Empty state with drag and drop */}
      {!isAwaitingAssets && !isAssetsError && !hasRows && (
        <div
          className={cn(
            "w-full flex flex-col justify-center gap-6 py-9 h-96 items-center border-2 border-dashed border-base-300 rounded-lg",
            isDragging && "border-primary bg-base-200",
          )}
          {...dragBindings}
        >
          <video src={dragDropVideoUrl} className={cn("w-36", dragDropVideoClassName)} autoPlay muted loop />
          {!isDragging && (
            <div className="flex flex-col gap-3 items-center">
              <h2>Drag & drop</h2>
              <p className="text-base-content/70">{dragDropText}</p>
            </div>
          )}

          {isDragging && <p>Drop files here</p>}
          <Button variant="primary" onClick={handleClick}>
            {selectAssetsText}
          </Button>
        </div>
      )}

      {/* Uploads — the table stays a drop target so more assets can be dragged in after the first set */}
      {hasRows && !isAwaitingAssets && (
        <div
          className={cn(
            // Transparent border reserves the space so the layout doesn't shift when the drag highlight appears
            "grow flex flex-row flex-wrap w-full gap-4 p-2 rounded-lg border-2 border-dashed border-transparent",
            isDragging && "border-primary bg-base-200",
          )}
          {...dragBindings}
        >
          <Table>
            <thead>
              <TableRow>
                <TableHeader className="w-[50%]">{firstColumnHeader}</TableHeader>
                <TableHeader className="w-40">Size</TableHeader>
                <TableHeader className="w-40">Dimensions</TableHeader>
                {isVideo && <TableHeader className="w-36">Aspect ratio</TableHeader>}
                {isVideo && <TableHeader className="w-36">Duration</TableHeader>}
                <TableHeader className="w-[30%]">Status</TableHeader>
                <TableHeader className="w-32"></TableHeader>
              </TableRow>
            </thead>
            <tbody>
              {pendingUploads.map((upload) => (
                <TableRow key={upload.key} className="group hover:bg-base-100/50 cursor-pointer">
                  <TableCell truncate={false}>
                    {upload.status !== "invalid" ? (
                      <div className="skeleton w-16 h-16 rounded-lg bg-base-300"></div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-base-300 flex items-center justify-center">
                        <EyeSlashIcon className="size-7 text-error" />
                      </div>
                    )}
                    <EditElement defaultValue={upload.name} elementId={upload.key} handleElementEdit={handleRenameUploading}>
                      <p className="truncate">{upload.name}</p>
                    </EditElement>
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  {isVideo && <TableCell>-</TableCell>}
                  {isVideo && <TableCell>-</TableCell>}
                  <TableCell truncate={false}>
                    {upload.status === "uploading" && (
                      <div className="flex items-center gap-2 text-base-content/70">
                        <Progress value={upload.progress ?? 0} staticColorClass="bg-success" className="w-40" />
                        {upload.progress ?? 0}%
                      </div>
                    )}
                    {upload.status === "finishing" && (
                      <div className="flex items-center gap-2 text-base-content/70">
                        <Loading size="sm" />
                        Finishing...
                      </div>
                    )}
                    {upload.status === "failed" && (
                      <div className="flex items-start gap-2 text-error">
                        <ExclamationCircleIcon className="size-4 shrink-0" />
                        {upload.errorMessage ?? "We had an issue uploading this asset, please try again"}
                      </div>
                    )}
                    {upload.status === "invalid" && (
                      <div className="flex items-start gap-2 text-error">
                        <ExclamationCircleIcon className="size-4 shrink-0" />
                        {upload.errorMessage ?? "This asset is invalid and cannot be uploaded."}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="justify-end">
                    {upload.status === "failed" && (
                      <Button className="h-8 min-h-8 max-h-8" onClick={() => handleRetry(upload.key)}>
                        <ArrowPathIcon className="size-4" />
                        Retry
                      </Button>
                    )}
                    <TrashIcon className="size-4" onClick={() => handleRemoveUploading(upload)} />
                  </TableCell>
                </TableRow>
              ))}

              {/* Existing Assets */}
              {existingAssets.map((asset) => (
                <TableRow key={asset.id} className="group hover:bg-base-100/50 cursor-pointer">
                  <TableCell truncate={false}>
                    <img
                      src={
                        isVideo
                          ? getVideoThumbnailUrl({ projectId, studyId, assetKey: asset.pipelineKey })
                          : `${ENV.API_PLATFORM_URL}/pipeline/renderer/images/v1?prefix=${projectId}/${studyId}&key=${asset.pipelineKey}&maxWidth=150&maxHeight=150`
                      }
                      className="rounded-lg w-16 h-16 object-cover"
                    />
                    <EditElement defaultValue={asset.name} elementId={asset.id} handleElementEdit={(_, newName) => handleRenameExisting(asset, newName)}>
                      <p className="truncate">{asset.name}</p>
                    </EditElement>
                  </TableCell>
                  <TableCell>{asFileSize(asset.sizeInBytes)}</TableCell>
                  <TableCell>{`${asset.width} × ${asset.height} px`}</TableCell>
                  {isVideo && <TableCell>{asAspectRatio(asset.aspectRatio)}</TableCell>}
                  {isVideo && <TableCell>{asDuration(asset.durationInSeconds)}</TableCell>}
                  <TableCell>
                    {deletingAssetIds.has(asset.id) ? (
                      <div className="flex items-center gap-2 text-base-content/70">
                        <Loading size="sm" />
                        Deleting...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-success">
                        <CheckCircleIcon className="size-4" />
                        Ready
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="justify-end">
                    <TrashIcon
                      className={cn("size-4", deletingAssetIds.has(asset.id) ? "opacity-40 pointer-events-none" : "cursor-pointer")}
                      onClick={() => handleRemoveExisting(asset)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Hidden */}
      <input
        multiple
        type="file"
        accept={acceptedFileTypes.join(", ")}
        className="hidden"
        ref={inputRef}
        onChange={(e) => {
          void handleAdd(Array.from(e.target.files ?? []));
        }}
      />
    </>
  );
};
